import { Box } from '@mui/material'
import FormControl from '@mui/material/FormControl'
import MUIInput from '@mui/material/Input'
import { InputBaseComponentProps, InputBaseProps } from '@mui/material/InputBase'
import InputLabel, { InputLabelProps } from '@mui/material/InputLabel'
import { styled as muiStyled } from '@mui/material/styles'
import MUITextField, { TextFieldProps as MUITextFieldProps } from '@mui/material/TextField'
import React, { useEffect, useRef } from 'react'

import { AutosizeTextArea, AutosizeTextAreaResizeProps } from '@/atoms/AutosizeTextArea'
import { Asterisk } from '@/atoms/GenericComponents'
import { E2E, passTestId } from '@/lib/e2e-utils'
import { removeProps } from '@/lib/react'
import { autoCompleteOff, randomBetween } from '@/lib/utils'
import { backgroundGray, mainBlack, secondaryBlack } from '@/refactor/colors'
import styled, { css, font, muiUtils, padPx, textOverflow } from '@/styles'
import { DefaultProps, MUIFunctionComponent } from '@/travelsuit'

type InputDefaultKeys = 'onChange' | 'onFocus' | 'onBlur' | 'readOnly' | 'disabled'

export type InputComponentType = React.ComponentProps<typeof MUIInput>['inputComponent']

export type TextFieldProps = DefaultProps &
	Omit<
		MUITextFieldProps & AutosizeTextAreaResizeProps,
		'ref' | 'color' | 'onChange' | 'onFocus' | 'onBlur' | 'variant' | 'width' | 'classes' | 'slotProps'
	> &
	Pick<React.InputHTMLAttributes<HTMLInputElement | HTMLTextAreaElement>, InputDefaultKeys>

export interface IProps extends TextFieldProps, E2E {
	multiline?: boolean
	error?: boolean
	leading?: React.ReactNode
	trailing?: React.ReactNode
	disableSelectOnFocus?: boolean
	label?: React.ReactNode
	labelOffset?: number | string
	labelOffsetShrink?: number | string
	inputRef?:
		| ((ref: HTMLInputElement | HTMLTextAreaElement) => void)
		| React.MutableRefObject<HTMLInputElement>
		| React.RefObject<HTMLInputElement>
		| React.Ref<HTMLInputElement | HTMLTextAreaElement>
	disabled?: boolean
	hideLabel?: boolean
	shrinkLabel?: boolean
	name?: string
	inputComponent?: InputComponentType
	/**
	 * @deprecated Use slotProps.input instead
	 */
	inputProps?: TextFieldProps['inputProps']
	slotProps?: MUITextFieldProps['slotProps'] | InputBaseProps['slotProps']
	isRounded?: boolean
	hideStepperArrows?: boolean
	allowNegativeNumbers?: boolean
	prefixWidth?: number
}

const PREFIX = 'TextField'
const classes = {
	inputRootWithIcon: `${PREFIX}-inputRootWithIcon`,
	labelRootWithIcon: `${PREFIX}-labelRootWithIcon`,
	labelShrinkWithIcon: `${PREFIX}-labelShrinkWithIcon`,
}
interface StyledMUITextFieldProps extends Pick<IProps, 'hideStepperArrows' | 'isRounded'> {
	labelOffset?: number | string
	labelOffsetShrink?: number | string
	hideLabel?: boolean
	hasLeadingIcon?: boolean
}

const StyledMUITextField = styled(
	removeProps(MUITextField)<StyledMUITextFieldProps>(
		'labelOffset',
		'labelOffsetShrink',
		'hideLabel',
		'hideStepperArrows',
		'isRounded',
		'hasLeadingIcon',
	),
)`
	${({
		labelOffset: $labelOffset,
		labelOffsetShrink: $labelOffsetShrink,
		hideLabel: $hideLabel,
		hasLeadingIcon: $hasLeadingIcon,
		hideStepperArrows: $hideStepperArrows,
		isRounded: $isRounded,
		value,
		theme,
	}) => css`
		& .MuiInputLabel-root {
			width: 100%;
			left: ${$labelOffset !== undefined ? padPx($labelOffset) : ''};
			white-space: nowrap;
			margin-top: ${$hideLabel ? '-15px' : ''};
		}

		.MuiInputLabel-root {
			display: ${$hideLabel && value ? 'none' : ''};
		}

		.MuiInputLabel-root:not(.MuiFormLabel-filled) {
			max-width: calc(100% - ${theme.spacing($hasLeadingIcon ? 10 : 6)});
			${textOverflow()}
		}

		& .MuiFormLabel-filled > .MuiBox-root > .MuiBox-root {
			max-width: initial;
		}

		.MuiInputBase-root {
			margin-top: ${$hideLabel ? '0' : ''};
		}

		& .MuiInputLabel-shrink {
			left: ${$labelOffsetShrink !== undefined ? padPx($labelOffsetShrink) : ''};
		}

		.MuiInputBase-input {
			border-radius: ${$isRounded ? '2em' : undefined};
			height: 1.2855em;
		}

		${$hideStepperArrows &&
		css`
			.MuiInputBase-input {
				appearance: textfield;
				-moz-appearance: textfield;
				-webkit-appearance: none;
			}

			.MuiInputBase-input::-webkit-inner-spin-button,
			.MuiInputBase-input::-webkit-outer-spin-button {
				appearance: none;
				-webkit-appearance: none;
				margin: 0;
			}
		`}

		${muiUtils.mobile} {
			.MuiInputBase-input:hover:not(:disabled):not(.Mui-disabled) {
				box-shadow: none;
			}

			.MuiInputBase-input:focus {
				background: ${backgroundGray};
			}
		}
	`}
`

const StyledInputLabel = styled(({ offset, offsetShrink, multiline, ...rest }) => <InputLabel {...rest} />)<{
	offset?: number | string
	offsetShrink?: number | string
	multiline?: boolean
}>`
	${({ offset: $offset, offsetShrink: $offsetShrink, multiline: $multiline }) => css`
		&.MuiInputLabel-root {
			left: ${$offset !== undefined ? padPx($offset) : ''};
		}

		&.MuiInputLabel-shrink {
			left: ${$offsetShrink !== undefined ? padPx($offsetShrink) : ''};
		}

		&.MuiInputLabel-formControl:not(.MuiInputLabel-shrink) {
			top: ${$multiline ? '12px' : ''};
		}
	`}
`

const StyledMUIInput = styled(MUIInput)`
	& input,
	& textarea {
		padding: 13px 20px;
	}

	flex-direction: column;
	align-items: flex-start;
`

export const IconContainer = styled.div`
	position: absolute;
	left: 20px;
	z-index: 1;
	${muiUtils.mobile} {
		display: flex;
	}
`

const TextField: MUIFunctionComponent<IProps & E2E> = (props) => {
	const {
		className,
		multiline,
		leading,
		trailing,
		disableSelectOnFocus,
		onClick,
		inputRef,
		children,
		error,
		onFocus,
		onBlur,
		onChange,
		required,
		labelOffset,
		labelOffsetShrink,
		readOnly,
		rows,
		defaultValue,
		e2e: _e2e,
		'data-test': _dataTest,
		inputProps,
		hideLabel,
		shrinkLabel,
		inputComponent: InputComponent = AutosizeTextArea,
		hideStepperArrows,
		fullWidth = true,
		slotProps,
		...rest
	} = props
	const [shrink, setShrink] = React.useState<boolean>(Boolean(typeof rest.value === 'string' && rest.value?.length))
	const [dirty, setDirty] = React.useState<boolean>(false)
	const value = (rest.value || defaultValue) as string
	const shrinkVal = Boolean(shrink || value?.toString().length || shrinkLabel)
	const isRequired = dirty && required
	const textareaId = rest.id ?? 'txtra-' + randomBetween(1000000).toString()

	useEffect(() => {
		if (shrink && !value) {
			setShrink(false)
		}
	}, [shrink, value])

	const { endAdornment, endAdornmentWidth, label } = useTrailing(trailing, rest.label, required, shrinkLabel)

	const finalInputLabelProps: Partial<InputLabelProps> = {
		classes: leading
			? {
					root: classes.labelRootWithIcon,
					shrink: classes.labelShrinkWithIcon,
				}
			: undefined,
		shrink: shrinkVal,
		required: endAdornmentWidth ? false : required,
		error,
	}

	const finalInputProps: Partial<Omit<InputBaseComponentProps, 'color'>> = {
		classes: { input: leading ? classes.inputRootWithIcon : undefined },
		startAdornment: leading ? <IconContainer>{leading}</IconContainer> : undefined,
		endAdornment,
		required: isRequired,
		readOnly,
		error,
	}

	const wrappedInputHandlers = {
		inputRef:
			typeof inputRef === 'function'
				? (newRef: HTMLInputElement | HTMLTextAreaElement) => {
						inputRef?.(newRef)
					}
				: inputRef,
		onFocus(e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) {
			e.persist()
			if (!disableSelectOnFocus) {
				e.target.select()
			}
			setShrink(true)
			if (onFocus) {
				onFocus(e)
			}
		},
		onBlur(e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) {
			e.persist()
			setTimeout(() => {
				if (e.target.value.length === 0) {
					setShrink(false)
				}
			}, 70)
			if (onBlur) {
				onBlur(e)
			}
		},
		onChange(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) {
			e.persist()
			setShrink(e.target.value.length !== 0)
			setDirty(true)
			if (onChange) {
				onChange(e)
			}
		},
		onClick,
	}

	return multiline ? (
		<FormControl fullWidth={fullWidth} variant="standard">
			{rest.label ? (
				<StyledInputLabel
					htmlFor={textareaId}
					offset={labelOffset}
					offsetShrink={labelOffsetShrink}
					multiline
					{...finalInputLabelProps}
				>
					{rest.label}
				</StyledInputLabel>
			) : null}
			<StyledMUIInput
				inputComponent={InputComponent}
				className={className}
				id={textareaId}
				{...wrappedInputHandlers}
				autoComplete={autoCompleteOff()}
				error={error}
				fullWidth={fullWidth}
				required={isRequired}
				inputProps={{
					...finalInputProps,
					'data-test': passTestId(props),
					...(rest as InputBaseComponentProps),
					fullWidth,
				}}
				value={value}
				rows={rows}
				defaultValue={defaultValue}
				multiline
				slotProps={slotProps as InputBaseProps['slotProps']}
			/>
		</FormControl>
	) : (
		<StyledMUITextField
			variant="standard"
			hasLeadingIcon={!!leading}
			hideLabel={hideLabel}
			className={className}
			{...wrappedInputHandlers}
			onPaste={(e) => {
				if (rest.type === 'number') {
					e.persist()

					const value = e.clipboardData
						.getData('Text')
						.replace(props.allowNegativeNumbers ? /[^0-9.,-]/g : /[^0-9.,]/g, '')

					if (value !== e.clipboardData.getData('Text')) {
						e.preventDefault()
						e.stopPropagation()

						if (onChange) {
							onChange({
								target: {
									value,
								},
							} as React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>)
						}

						return false
					}
				}
				return true
			}}
			onKeyDown={(e) => {
				if (rest.type === 'number') {
					e.persist()

					const keyCodesOfNonnumericSymbols = [187, 173, 107, 109, 69]

					if ([...keyCodesOfNonnumericSymbols, ...(props.allowNegativeNumbers ? [] : [189])].includes(e.keyCode)) {
						e.preventDefault()
						e.stopPropagation()
						return false
					}
				}

				return true
			}}
			autoComplete={autoCompleteOff()}
			error={error}
			hideStepperArrows={hideStepperArrows ?? false}
			slotProps={{
				...slotProps,
				input: {
					...finalInputProps,
					...(slotProps as MUITextFieldProps['slotProps'])?.input,
				},
				htmlInput: {
					...(slotProps as MUITextFieldProps['slotProps'])?.htmlInput,
					...inputProps,
					'data-test': passTestId(props),
				},
				inputLabel: finalInputLabelProps,
				formHelperText: { 'data-test': passTestId(props, 'HelperText') },
			}}
			labelOffset={labelOffset}
			labelOffsetShrink={labelOffsetShrink}
			defaultValue={defaultValue}
			data-test={passTestId(props, 'Container')}
			{...rest}
			fullWidth={fullWidth}
			label={label}
		/>
	)
}

TextField.muiName = (MUITextField as MUIFunctionComponent).muiName

export const DashTextField = styled(TextField).attrs(() => ({ multiline: true }))<{
	dashColor?: string
	dashWidth?: number
}>`
	& .MuiInputBase-input {
		background: white;
		border-bottom: ${({ dashWidth: $dashWidth, dashColor: $dashColor }) =>
			`${padPx($dashWidth ?? 1)} dashed ${$dashColor ?? mainBlack}`};

		&,
		&:hover,
		&:active,
		&:focus {
			box-shadow: none !important;
		}
	}
`

export const TrailingText = styled.span`
	position: absolute;
	right: 20px;
	top: 50%;
	transform: translateY(-50%);
	color: ${secondaryBlack};
	${font({ size: 14 })}
`

export default muiStyled(TextField)(() => ({
	[`& .${classes.inputRootWithIcon}`]: {
		paddingLeft: 46,
	},
	[`& .${classes.labelRootWithIcon}`]: {
		left: 46,
	},
	[`& .${classes.labelShrinkWithIcon}`]: {
		left: 0,
	},
}))

function useTrailing(
	trailing: IProps['trailing'],
	label: IProps['label'],
	required: IProps['required'],
	shrinkLabel: IProps['shrinkLabel'],
) {
	const ref = useRef<HTMLSpanElement>(null)
	const trailingWidth = ref.current?.clientWidth ?? 0
	const endAdornmentWidth = trailingWidth && trailingWidth + 25

	return {
		endAdornment: <TrailingText ref={ref}>{trailing}</TrailingText>,
		endAdornmentWidth,
		label: trailingWidth ? (
			<Box display="flex" gap="2px">
				<Box display="inline-flex" maxWidth={shrinkLabel ? 'initial ' : `calc(100% - ${endAdornmentWidth}px)`}>
					<Box component="span" overflow="hidden" textOverflow="ellipsis" whiteSpace="nowrap" minWidth={0}>
						{label}
					</Box>
				</Box>
				{required && <Asterisk>*</Asterisk>}
			</Box>
		) : (
			label
		),
	}
}
